import axios from 'services/axios'
import {
  API_PAYMENT_CONFIRM_RECEIVED,
  API_PAYMENT_EXPORT,
  API_PAYMENT_INFO,
  API_PAYMENT_LIST_EXPORT,
  API_PAYMENT_OR_DOWNLOAD,
  API_PAYMENT_OR_LIST,
  API_PAYMENT_OR_REMOVE,
  API_PAYMENT_OR_UPLOAD,
  API_PAYMENT_POP_DOWNLOAD,
} from 'constants/endpoints'

// Helpers
import { date, datetime } from 'helpers/format'

// Models
import PaymentBreakdownModel from 'models/PaymentBreakdown'

/**
 * Get Payment Infos
 *
 * @param {Object} { paymentId, paymentMethod, notificationId }
 * @return {Object}
 */
export const getPaymentInfo = async ({
  paymentId,
  paymentMethod,
  notificationId = null,
}) => {
  try {
    const { data } = await axios.post(API_PAYMENT_INFO, {
      id: paymentId,
      paymentMethod,
      notifiId: notificationId,
    })

    return new PaymentBreakdownModel(data)
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Get Payment Infos
 *
 * @param {Number} paymentId
 * @return {Object}
 */
export const confirmPaymentReceived = async (paymentId) => {
  try {
    const { data } = await axios.post(API_PAYMENT_CONFIRM_RECEIVED, {
      paymentId,
    })

    return datetime(data)
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Download payment selected
 *
 * @param {Object} { filters, typeFile, sortBy, sortDirection }
 * @return {Object}
 */
export const downloadSelectedPayments = async ({
  filters,
  typeFile,
  sortBy,
  sortDirection,
}) => {
  try {
    await axios.post(
      API_PAYMENT_LIST_EXPORT,
      {
        ...filters,
        typeFile,
        sortBy,
        sortDirection,
      },
      {
        downloadFile: true,
      }
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Download payment breakdown
 *
 * @param {Object} { paymentId, paymentMethod, typeFile }
 * @returns {Object}
 */
export const downloadPayment = async ({
  paymentId,
  paymentMethod,
  typeFile,
}) => {
  try {
    return await axios.post(
      API_PAYMENT_EXPORT,
      { id: paymentId, paymentMethod, typeFile },
      {
        downloadFile: true,
      }
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Download payment breakdown
 *
 * @param {Object} { paymentId, fileName }
 * @returns {Object}
 */
export const downloadPaymentPOP = async ({ paymentId, fileName }) => {
  try {
    return await axios.post(
      API_PAYMENT_POP_DOWNLOAD,
      {
        paymentId,
        fileName,
      },
      {
        downloadFile: true,
      }
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Get uploaded OR
 *
 * @param {Number} paymentId
 * @return {Array}
 */
export const getUploadedOR = async (paymentId) => {
  try {
    const { data } = await axios.post(
      API_PAYMENT_OR_LIST,
      {},
      {
        params: { paymentId },
      }
    )
    return data.map((x) => {
      return {
        name: x.fileName,
        uploadedAt: date(x.createDate, 'MMM dd, yyyy hh:mma'),
      }
    })
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Payment Or Download
 *
 * @param {Number} { paymentId, fileName }
 * @return {Array}
 */
export const paymentORDownload = async ({ paymentId, fileName }) => {
  try {
    return await axios.post(
      API_PAYMENT_OR_DOWNLOAD,
      {
        paymentId,
        fileName,
      },
      {
        downloadFile: true,
      }
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Payment Or Remove File
 *
 * @param {Number} paymentId
 * @param {String} fileName
 * @return {Array}
 */
export const paymentORRemove = async ({ paymentId, fileName }) => {
  try {
    const { data } = await axios.post(API_PAYMENT_OR_REMOVE, {
      mode: 'OR',
      mappingId: paymentId,
      fileName,
    })
    return data
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Upload OR file
 *
 * @param {File} file
 * @param {Number} paymentId
 * @return {Object}
 */
export const paymentORUpload = async ({
  file,
  paymentId,
  onUploadProgress,
}) => {
  try {
    const { data } = await axios.post(
      API_PAYMENT_OR_UPLOAD,
      {
        file,
        paymentId,
      },
      { onUploadProgress }
    )

    return {
      name: data.fileName,
      uploadedAt: date(data.uploadDate, 'MMM dd, yyyy hh:mma'),
    }
  } catch (error) {
    return Promise.reject(error)
  }
}
